/**
 * Connector-Header-Karte für den Channels-Editor (C2).
 *
 * Channel-Name · Integrations-Badge (z.B. JTL) · Connector-Version ·
 * Abweichungs-Count · Toggle „Vererbte Felder zeigen" · „Sync from JTL" · ⋮-Menü.
 */

import { useTranslation } from 'react-i18next'
import { Plug, ArrowLeftRight, RefreshCw, MoreVertical } from 'lucide-react'
import {
  Badge,
  Button,
  Loading,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'

interface Props {
  channelName: string
  integrationType?: string
  connectorVersion?: string
  overrideCount: number
  showInherited: boolean
  onToggleInherited: (value: boolean) => void
  onSync: () => void
  isSyncing: boolean
}

export function ConnectorHeaderCard({
  channelName,
  integrationType,
  connectorVersion,
  overrideCount,
  showInherited,
  onToggleInherited,
  onSync,
  isSyncing,
}: Props) {
  const { t } = useTranslation()

  return (
    <section
      className="content-connector-header flex items-center justify-between gap-3 flex-wrap rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 mx-3 mt-3"
      aria-label={t('productContent.channels.connector.title')}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="flex h-7 w-7 items-center justify-center rounded bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400">
          <Plug className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="font-semibold text-neutral-900 dark:text-neutral-50 truncate">{channelName}</span>
        {integrationType && <Badge variant="secondary">{integrationType.toUpperCase()}</Badge>}
        {connectorVersion && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{connectorVersion}</span>
        )}
        {overrideCount > 0 && (
          <span className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400">
            <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
            {t('productContent.channels.connector.deviations', { count: overrideCount })}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        <label htmlFor={EID['content-connector-show-inherited-toggle']} className="flex items-center gap-1.5 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            id={EID['content-connector-show-inherited-toggle']}
            checked={showInherited}
            onChange={(e) => onToggleInherited(e.target.checked)}
            className="accent-primary-500"
          />
          {t('productContent.channels.connector.showInherited')}
        </label>
        <Button
          variant="outline"
          size="sm"
          id={EID['content-connector-sync-btn']}
          onClick={onSync}
          disabled={isSyncing}
        >
          {isSyncing ? <Loading size="sm" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          {t('productContent.channels.connector.syncFromJtl')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" id={EID['content-connector-menu-btn']} aria-label={t('productContent.channels.connector.moreActions')}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onSync}>{t('productContent.channels.connector.syncFromJtl')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </section>
  )
}
